import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * PublicCTAGroup — standardized primary + secondary CTA row.
 *
 * Props:
 *  - primary:   { label, href, onClick, testId, external }
 *  - secondary: { label, href, onClick, testId, external }
 *  - align: 'left' | 'center'
 */
export default function PublicCTAGroup({ primary, secondary, align = 'left' }) {
  const alignClass =
    align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${alignClass}`}>
      {primary && (
        <CTAButton
          {...primary}
          variant="primary"
          testId={primary.testId || 'public-cta-primary'}
        />
      )}
      {secondary && (
        <CTAButton
          {...secondary}
          variant="secondary"
          testId={secondary.testId || 'public-cta-secondary'}
        />
      )}
    </div>
  );
}

function CTAButton({ label, href, onClick, external, testId, variant }) {
  const base =
    'inline-flex items-center justify-center h-12 px-7 sm:px-8 rounded-full text-base font-semibold transition-[transform,background-color,border-color,box-shadow] duration-200 hover:-translate-y-0.5';
  const styles =
    variant === 'primary'
      ? 'bg-primary hover:bg-primary-hover text-primary-foreground hover:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]'
      : 'bg-transparent hover:bg-foreground hover:text-background text-foreground border border-foreground/30';

  const content = (
    <>
      {label}
      {variant === 'primary' && <ArrowRight className="w-4 h-4 ml-2" />}
    </>
  );

  if (onClick && !href) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={`${base} ${styles}`}
      >
        {content}
      </button>
    );
  }

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        data-testid={testId}
        className={`${base} ${styles}`}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={href} onClick={onClick} data-testid={testId} className={`${base} ${styles}`}>
      {content}
    </Link>
  );
}
